import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { AlertCircle, Check, ChevronDown, Download, FileVideo, Plus, Upload, X } from 'lucide-react';

import { AuthorFilter } from '../../components/AuthorFilter';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Button, Pill, Seg, Slider, type PillTone } from '../../components/ui';
import {
  SUBTITLE_MAX_FILES,
  SUBTITLE_VIDEO_EXTS,
  isSubtitleBatchRunning,
  useCurrentUser,
  useRestyleSubtitles,
  useStartSubtitles,
  useSubtitleBatches,
  useSubtitleLayout,
  useSubtitlePresets,
  useSubtitleSettings,
} from '../../hooks';
import { subtitlesApi } from '../../api/endpoints';
import { authorLabel } from '../../lib/author';
import { downloadFile } from '../../lib/download';
import type {
  Id,
  SubtitleBatch,
  SubtitleBatchStatus,
  SubtitlePos,
  SubtitlePreset,
} from '../../types/api';
import styles from './Subtitles.module.css';

const SAMPLE = ['успей', 'забрать', 'бонус'];
const SAMPLE_WORD = 'забрать';
const DEFAULT_PRESET = 'tiktok_white';
const MAX_VIDEO_MB = 200;
const SIZE_MIN = 0.5;
const SIZE_MAX = 2.5;
const SIZE_STEP = 0.05;
// Browsers ask once for several downloads, then drop clicks that come too fast.
const DOWNLOAD_GAP_MS = 350;

const PHASES: { key: SubtitleBatchStatus; label: string; weight: number }[] = [
  { key: 'pending', label: 'В очереди', weight: 1 },
  { key: 'transcribing', label: 'Распознаём речь', weight: 3 },
  { key: 'rendering', label: 'Рендерим', weight: 8 },
  { key: 'done', label: 'Готово', weight: 1 },
];

const STAGE_LABELS: Record<SubtitleBatchStatus, string> = {
  pending: 'в очереди',
  transcribing: 'распознаём',
  rendering: 'рендерим',
  done: 'готово',
  failed: 'ошибка',
};

const STAGE_TONES: Record<SubtitleBatchStatus, PillTone> = {
  pending: 'info',
  transcribing: 'info',
  rendering: 'info',
  done: 'ok',
  failed: 'danger',
};

const FX_LABELS: Record<string, string> = {
  none: 'статично',
  pop: 'выпрыгивание',
  fade: 'проявление',
  karaoke: 'караоке',
  line_accent: 'слово в строке',
  word: 'по одному слову',
};

type StageFilter = 'all' | 'active' | 'done' | 'failed';

const FILTERS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'В работе' },
  { value: 'done', label: 'Готовые' },
  { value: 'failed', label: 'Ошибки' },
];

const matchesFilter = (batch: SubtitleBatch, filter: StageFilter): boolean =>
  filter === 'all'
    ? true
    : filter === 'active'
      ? isSubtitleBatchRunning(batch)
      : filter === 'done'
        ? batch.status === 'done'
        : batch.status === 'failed' || batch.failed > 0;

function ago(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} ч`;
  return `${Math.round(hours / 24)} дн`;
}

function humanBytes(n: number): string {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`;
  const mb = n / (1024 * 1024);
  return mb < 1024 ? `${mb.toFixed(mb < 10 ? 1 : 0)} МБ` : `${(mb / 1024).toFixed(2)} ГБ`;
}

function daysLabel(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  if (tens >= 11 && tens <= 14) return `${n} дней`;
  if (ones === 1) return `${n} день`;
  if (ones >= 2 && ones <= 4) return `${n} дня`;
  return `${n} дней`;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const [r, g, b] = [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)].map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Approximates the libass look in CSS; sizes are in container width units, like the preset's
// percent of the frame width.
function previewStyle(preset: SubtitlePreset, sizeMult = 1): CSSProperties {
  const alpha = preset.back_alpha / 255;
  const css: CSSProperties = {
    fontFamily: `"${preset.font_family}", sans-serif`,
    fontSize: `${preset.size_pct * sizeMult}cqw`,
    color: preset.primary,
    textTransform: preset.uppercase ? 'uppercase' : 'none',
    WebkitTextStrokeWidth: preset.outline_w > 0 ? `${preset.outline_w * 2}cqw` : '0',
    WebkitTextStrokeColor: preset.outline,
  };
  if (preset.border_style === 3) {
    css.background = hexToRgba(preset.back, alpha);
    css.padding = '1.5cqw 2.5cqw';
    css.borderRadius = '1cqw';
  } else if (preset.shadow > 0) {
    css.textShadow = `${preset.shadow}cqw ${preset.shadow}cqw ${preset.shadow * 1.5}cqw ${hexToRgba(preset.back, alpha || 1)}`;
  }
  return css;
}

function alignClass(align: number): string | undefined {
  if (align >= 7) return styles.alignTop;
  if (align >= 4) return styles.alignMiddle;
  return styles.alignBottom;
}

// Same formula as the backend's default position, so the dragged line starts where the
// preset would put it.
function defaultPos(preset: SubtitlePreset | undefined, aspect: number, size: number): SubtitlePos {
  if (!preset) return { x: 50, y: 84 };
  const half = 0.6 * preset.size_pct * size * aspect;
  if (preset.align >= 7) return { x: 50, y: preset.margin_pct + half };
  if (preset.align >= 4) return { x: 50, y: 50 };
  return { x: 50, y: 100 - preset.margin_pct - half };
}

const clampPct = (value: number) => Math.max(2, Math.min(98, value));

function useFitWords(
  words: string[],
  css: CSSProperties,
  hostRef: RefObject<HTMLElement | null>,
  frac: number,
): string[] {
  const [count, setCount] = useState(words.length);
  const key = words.join(' ');
  const cssKey = JSON.stringify(css);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || words.length === 0) return;
    const probe = document.createElement('span');
    Object.assign(probe.style, css as Record<string, string>);
    probe.style.position = 'absolute';
    probe.style.left = '-9999px';
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'nowrap';
    host.appendChild(probe);
    const max = host.clientWidth * frac;
    let n = words.length;
    while (n > 1) {
      probe.textContent = words.slice(0, n).join(' ');
      if (probe.offsetWidth <= max) break;
      n -= 1;
    }
    host.removeChild(probe);
    setCount(n);
    // cssKey stands in for css, which is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, cssKey, frac, hostRef]);

  return words.slice(0, Math.max(1, Math.min(count, words.length)));
}

function isVideo(file: File): boolean {
  const name = file.name.toLowerCase();
  return SUBTITLE_VIDEO_EXTS.some((ext) => name.endsWith(ext));
}

export function SubtitlesPage() {
  const { can, isAdmin } = useCurrentUser();
  const canEdit = can('editor');

  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<StageFilter>('all');
  const [authorFilter, setAuthorFilter] = useState('');
  const [selected, setSelected] = useState<Id | 'new'>('new');
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [newPreset, setNewPreset] = useState(DEFAULT_PRESET);
  const [sharedTrack, setSharedTrack] = useState(true);
  const [newSize, setNewSize] = useState(1);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);
  const [tuneIdx, setTuneIdx] = useState<number | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  const presetsQ = useSubtitlePresets();
  const retentionDays = useSubtitleSettings().data?.retention_days;
  const presets = useMemo(() => presetsQ.data ?? [], [presetsQ.data]);
  const batchesQ = useSubtitleBatches({ limit: 100, author: authorFilter || undefined });
  const batches = useMemo(() => batchesQ.data?.items ?? [], [batchesQ.data]);
  const shown = batches.filter((b) => matchesFilter(b, filter));
  const batch = selected === 'new' ? null : (batches.find((b) => b.id === selected) ?? null);

  const start = useStartSubtitles((p) => setUploadPct(Math.round(p.fraction * 100)));
  const restyle = useRestyleSubtitles();

  const onAddFiles = useCallback((list: FileList | File[]) => {
    const picked = Array.from(list).filter(isVideo);
    if (picked.length === 0) return;
    setSelected('new');
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => f.name));
      return [...prev, ...picked.filter((f) => !seen.has(f.name))].slice(0, SUBTITLE_MAX_FILES);
    });
  }, []);

  useEffect(() => {
    setTuneIdx(null);
    setPendingPreset(null);
    setShowTranscript(false);
    restyle.reset();
    // restyle.reset is stable; listing it would reset on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const submit = () => {
    setUploadPct(0);
    start.mutate(
      { files, preset: newPreset, shared_track: sharedTrack, size: newSize },
      {
        onSuccess: (created) => {
          setFiles([]);
          setSelected(created.id);
        },
        onSettled: () => setUploadPct(null),
      },
    );
  };

  const downloadAll = () => {
    if (!batch) return;
    batch.items
      .filter((item) => item.output)
      .forEach((item, n) => {
        window.setTimeout(() => {
          void downloadFile(item.output?.url ?? '', item.output_name ?? item.filename);
        }, n * DOWNLOAD_GAP_MS);
      });
  };

  const activePresetKey = batch ? (pendingPreset ?? batch.preset) : newPreset;
  const activePreset = presets.find((p) => p.key === activePresetKey);
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  const uploading = start.isPending;
  const running = isSubtitleBatchRunning(batch ?? undefined);
  const readyCount = batch ? batch.items.filter((i) => i.output).length : 0;
  const editable = Boolean(batch) && canEdit && !running && !batch?.expired;

  const headerHint = batch
    ? `${batch.title} · ${STAGE_LABELS[batch.status]} · ${batch.done}/${batch.total}`
    : files.length > 0
      ? `${files.length} файлов к отправке · ${humanBytes(totalBytes)}`
      : 'распознаём речь один раз и вжигаем субтитры во все ролики пачки';

  return (
    <div className={styles.page}>
      <style>
        {presets
          .map(
            (p) =>
              `@font-face{font-family:"${p.font_family}";src:url("${subtitlesApi.fontUrl(p.font_file)}") format("truetype");font-display:swap;}`,
          )
          .join('\n')}
      </style>

      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Субтитры</h1>
          <p className={styles.hint}>{headerHint}</p>
        </div>
        <div className={styles.headerTools}>
          {isAdmin && <AuthorFilter value={authorFilter} onChange={setAuthorFilter} />}
          <Seg value={filter} onChange={setFilter} options={FILTERS} ariaLabel="Фильтр пачек" />
        </div>
      </header>

      <div className={styles.layout}>
        <aside className={styles.batches}>
          <div className={styles.batchesHead}>
            <span className={styles.blockTitle}>пачки</span>
            <span className={styles.blockNote}>{shown.length}</span>
            <Button
              size="xs"
              className={styles.newBtn}
              onClick={() => {
                setSelected('new');
                setTuneIdx(null);
              }}
            >
              <Plus size={12} />
              Новая
            </Button>
          </div>

          {batchesQ.error && (
            <ErrorBanner error={batchesQ.error} onRetry={() => void batchesQ.refetch()} />
          )}

          {shown.length === 0 ? (
            <div className={styles.batchesEmpty}>
              <b>Пачек пока нет</b>
              <span>
                Закиньте ролики справа — пачка появится здесь
                {retentionDays === undefined ? '' : ` и останется ${daysLabel(retentionDays)}`}.
              </span>
            </div>
          ) : (
            <div className={styles.batchList}>
              {shown.map((b) => (
                <BatchCard
                  key={b.id}
                  batch={b}
                  presetLabel={presets.find((p) => p.key === b.preset)?.label ?? b.preset}
                  picked={selected === b.id}
                  showAuthor={isAdmin}
                  onPick={() => setSelected(b.id)}
                />
              ))}
            </div>
          )}
        </aside>

        <section className={styles.pane}>
          {selected === 'new' && (
            <>
              <div
                className={`${styles.drop} ${dragActive ? styles.dropOn : ''} ${canEdit ? '' : styles.dropOff}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (canEdit) setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  if (canEdit) onAddFiles(e.dataTransfer.files);
                }}
                onClick={() => canEdit && inputRef.current?.click()}
                role="button"
                tabIndex={0}
                aria-disabled={!canEdit}
              >
                <Upload size={26} strokeWidth={1.5} />
                <b>
                  {canEdit
                    ? 'Перетащите ролики или выберите файлы'
                    : 'Роль «наблюдатель»: пачку может запустить редактор'}
                </b>
                <span>
                  mp4, mov или m4v, до {SUBTITLE_MAX_FILES} штук за раз, до {MAX_VIDEO_MB} МБ
                  каждый
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,.mp4,.mov,.m4v"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) onAddFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              {files.length > 0 && (
                <section className={styles.block}>
                  <div className={styles.blockHead}>
                    <span className={styles.blockTitle}>к отправке</span>
                    <span className={styles.blockNote}>
                      {files.length} файлов · {humanBytes(totalBytes)}
                    </span>
                  </div>
                  <div className={styles.fileRows}>
                    {files.map((f) => (
                      <div key={f.name} className={styles.fileRow}>
                        <FileVideo size={13} strokeWidth={1.5} />
                        <span className={styles.fileName} title={f.name}>
                          {f.name}
                        </span>
                        <span className={styles.blockNote}>{humanBytes(f.size)}</span>
                        {!uploading && (
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => setFiles((p) => p.filter((x) => x.name !== f.name))}
                            title="Убрать"
                            aria-label={`Убрать ${f.name}`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className={styles.optionRow}>
                    <div className={styles.optionText}>
                      <b>Размер субтитров</b>
                      <span className={styles.blockNote}>
                        применится ко всем роликам пачки, потом можно править поштучно
                      </span>
                    </div>
                    <div className={styles.optionControl}>
                      <SizeSlider value={newSize} onChange={setNewSize} disabled={uploading} />
                    </div>
                  </div>

                  <div className={styles.optionRow}>
                    <div className={styles.optionText}>
                      <b>Звуковая дорожка</b>
                      <span className={styles.blockNote}>
                        {sharedTrack
                          ? 'распознаём первый ролик, субтитры кладём на все'
                          : 'каждый ролик распознаётся отдельно'}
                      </span>
                    </div>
                    <Seg
                      value={sharedTrack ? 'shared' : 'own'}
                      onChange={(v) => setSharedTrack(v === 'shared')}
                      options={[
                        { value: 'shared', label: 'Одна на всех' },
                        { value: 'own', label: 'У каждого своя' },
                      ]}
                      ariaLabel="Звуковая дорожка"
                    />
                  </div>

                  {uploading && (
                    <div className={styles.uploadBox}>
                      <div className={styles.uploadHead}>
                        <b>Загружаем на сервер — не закрывайте вкладку</b>
                        <span>{uploadPct ?? 0}%</span>
                      </div>
                      <div className={styles.uploadTrack}>
                        <div className={styles.uploadFill} style={{ width: `${uploadPct ?? 0}%` }} />
                      </div>
                    </div>
                  )}

                  {start.error && <ErrorBanner error={start.error} />}

                  <div className={styles.blockFoot}>
                    <Button
                      variant="primary"
                      onClick={submit}
                      disabled={uploading || !activePreset || !canEdit}
                    >
                      {uploading ? 'Отправляем…' : `Сгенерировать (${files.length})`}
                    </Button>
                  </div>
                </section>
              )}

              <PresetStrip
                presets={presets}
                value={newPreset}
                onChange={setNewPreset}
                sizeMult={newSize}
              />
            </>
          )}

          {batch && (
            <>
              <PhaseBar batch={batch} />

              {batch.error && (
                <div className={styles.failBox} role="alert">
                  <AlertCircle size={16} strokeWidth={1.5} />
                  <div>
                    <b>Пачка не собралась</b>
                    <span className={styles.failCode}>{batch.error}</span>
                  </div>
                </div>
              )}

              {batch.expired && (
                <div className={styles.expiredBox}>
                  Исходники удалены
                  {retentionDays === undefined ? '' : ` через ${daysLabel(retentionDays)}`}:
                  подгонка и смена стиля недоступны. Готовые ролики скачиваются, для правок нужна
                  перезаливка.
                </div>
              )}

              {!canEdit && (
                <p className={styles.blockNote}>
                  Роль «наблюдатель»: менять стиль и подгонять ролики может редактор.
                </p>
              )}

              <PresetStrip
                presets={presets}
                value={activePresetKey}
                onChange={(key) => editable && setPendingPreset(key === batch.preset ? null : key)}
                dirty={pendingPreset !== null && editable}
                applying={restyle.isPending}
                onApply={() =>
                  pendingPreset &&
                  restyle.mutate(
                    { batchId: batch.id, preset: pendingPreset },
                    { onSuccess: () => setPendingPreset(null) },
                  )
                }
                onReset={() => setPendingPreset(null)}
              />
              {restyle.error && <ErrorBanner error={restyle.error} />}

              <section className={styles.block}>
                <div className={styles.blockHead}>
                  <span className={styles.blockTitle}>ролики пачки</span>
                  <span className={styles.blockNote}>
                    готово {readyCount} из {batch.items.length}
                  </span>
                  <span className={styles.blockActions}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={downloadAll}
                      disabled={readyCount === 0}
                      title="Скачает готовые ролики по отдельности, браузер спросит разрешение на несколько файлов"
                    >
                      <Download size={12} />
                      Скачать всё ({readyCount})
                    </Button>
                  </span>
                </div>

                <div className={styles.clipRows}>
                  {batch.items.map((item, i) => (
                    <div
                      key={`${item.filename}-${i}`}
                      className={`${styles.clipRow} ${tuneIdx === i ? styles.clipRowOn : ''}`}
                    >
                      <span className={styles.clipThumb}>
                        {item.frame ? (
                          <img src={item.frame.url} alt="" loading="lazy" />
                        ) : (
                          <FileVideo size={13} strokeWidth={1.5} />
                        )}
                      </span>
                      <span className={styles.clipInfo}>
                        <span className={styles.fileName} title={item.filename}>
                          {item.filename}
                        </span>
                        <span
                          className={`${styles.clipNote} ${item.status === 'failed' ? styles.clipNoteErr : ''}`}
                        >
                          {item.status === 'failed'
                            ? (item.error ?? 'ошибка')
                            : item.output
                              ? 'готов'
                              : item.output_deleted
                                ? 'удалён по сроку хранения'
                                : running
                                ? 'в очереди'
                                : 'ждёт'}
                          {item.pos ? ' · подогнан' : ''}
                          {item.size !== 1 ? ` · ${item.size.toFixed(2)}×` : ''}
                        </span>
                      </span>
                      <span className={styles.clipActions}>
                        <Button
                          size="xs"
                          variant="ghost"
                          disabled={!editable || !item.frame || !item.source_alive}
                          onClick={() => setTuneIdx(tuneIdx === i ? null : i)}
                        >
                          Подогнать
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          disabled={!item.output}
                          onClick={() =>
                            item.output &&
                            void downloadFile(item.output.url, item.output_name ?? item.filename)
                          }
                          iconOnly
                          title="Скачать"
                          aria-label={`Скачать ${item.filename}`}
                        >
                          <Download size={13} />
                        </Button>
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              {batch.transcript && (
                <section className={styles.block}>
                  <button
                    type="button"
                    className={styles.trBtn}
                    onClick={() => setShowTranscript((v) => !v)}
                    aria-expanded={showTranscript}
                  >
                    <ChevronDown size={13} className={showTranscript ? styles.trOpen : ''} />
                    Расшифровка
                    {batch.language && <span className={styles.blockNote}>{batch.language}</span>}
                  </button>
                  {showTranscript && <div className={styles.trText}>{batch.transcript}</div>}
                </section>
              )}
            </>
          )}
        </section>

        {batch && tuneIdx !== null && editable && (
          <TunePanel
            batch={batch}
            index={tuneIdx}
            preset={activePreset}
            onPick={setTuneIdx}
            onClose={() => setTuneIdx(null)}
          />
        )}
      </div>
    </div>
  );
}

function BatchCard({
  batch,
  presetLabel,
  picked,
  showAuthor,
  onPick,
}: {
  batch: SubtitleBatch;
  presetLabel: string;
  picked: boolean;
  showAuthor: boolean;
  onPick: () => void;
}) {
  const current = PHASES.findIndex((p) => p.key === batch.status);
  return (
    <button
      type="button"
      className={`${styles.batchCard} ${picked ? styles.batchCardOn : ''}`}
      onClick={onPick}
    >
      <span className={styles.batchTop}>
        <b className={styles.batchTitle} title={batch.title}>
          {batch.title}
        </b>
        <span className={styles.batchAgo}>{ago(batch.created_at)}</span>
      </span>
      <span className={styles.batchChips}>
        {batch.geo && <Pill tone="neutral">{batch.geo}</Pill>}
        <Pill tone={STAGE_TONES[batch.status]}>{STAGE_LABELS[batch.status]}</Pill>
      </span>
      <span className={styles.batchBar}>
        {PHASES.map((p, i) => (
          <span
            key={p.key}
            className={`${styles.batchSeg} ${styles[`seg_${segState(batch, i, current)}`]}`}
            style={{ flexGrow: p.weight }}
          />
        ))}
      </span>
      <span className={styles.batchFoot}>
        <span>{presetLabel}</span>
        <span>
          {batch.done}/{batch.total}
        </span>
      </span>
      {showAuthor && batch.author && (
        <span className={styles.batchOwner}>{authorLabel(batch.author)}</span>
      )}
    </button>
  );
}

function segState(batch: SubtitleBatch, index: number, current: number): string {
  if (batch.status === 'failed') return 'fail';
  if (batch.status === 'done' || index < current) return 'done';
  return index === current ? 'now' : 'next';
}

function PresetTile({
  preset,
  picked,
  onPick,
  sizeMult = 1,
}: {
  preset: SubtitlePreset;
  picked: boolean;
  onPick: () => void;
  sizeMult?: number;
}) {
  const frameRef = useRef<HTMLSpanElement>(null);
  const css = previewStyle(preset, sizeMult);
  const sample = useMemo(() => (preset.effect === 'word' ? [SAMPLE_WORD] : SAMPLE), [preset.effect]);
  const shown = useFitWords(sample, css, frameRef, 0.94);
  const accentIdx = preset.effect === 'karaoke' || preset.effect === 'line_accent' ? 1 : -1;
  return (
    <button
      type="button"
      className={`${styles.tile} ${picked ? styles.tilePicked : ''}`}
      onClick={onPick}
      title={preset.hint}
      aria-pressed={picked}
    >
      <span ref={frameRef} className={`${styles.tileFrame} ${alignClass(preset.align) ?? ''}`}>
        <span className={styles.tileText} style={css}>
          {shown.map((word, i) => (
            <span
              key={i}
              style={
                i === accentIdx ? { color: preset.accent_cycle[0] ?? preset.accent } : undefined
              }
            >
              {word}
              {i < shown.length - 1 ? ' ' : ''}
            </span>
          ))}
        </span>
        <span className={styles.tileFx}>{FX_LABELS[preset.effect] ?? preset.effect}</span>
        {picked && (
          <span className={styles.tileCheck}>
            <Check size={11} />
          </span>
        )}
      </span>
      <span className={styles.tileName}>
        <span className={styles.tileLabel}>{preset.label}</span>
        {!preset.cyrillic && (
          <span
            className={styles.tileLatin}
            title="Латинский шрифт: на кириллице подменяется на похожий"
          >
            Aa→Аа
          </span>
        )}
      </span>
    </button>
  );
}

function PresetStrip({
  presets,
  value,
  onChange,
  dirty,
  onApply,
  onReset,
  applying,
  sizeMult = 1,
}: {
  presets: SubtitlePreset[];
  value: string;
  onChange: (key: string) => void;
  dirty?: boolean;
  onApply?: () => void;
  onReset?: () => void;
  applying?: boolean;
  sizeMult?: number;
}) {
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <span className={styles.blockTitle}>стиль субтитров</span>
        <span className={styles.blockNote}>{presets.length} пресетов</span>
        {dirty && (
          <span className={styles.blockActions}>
            <Button size="sm" variant="primary" onClick={onApply} disabled={applying}>
              {applying ? 'Перерендерим…' : 'Применить к пачке'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onReset} disabled={applying}>
              Отменить
            </Button>
          </span>
        )}
      </div>
      <div className={styles.tileRow}>
        {presets.map((p) => (
          <PresetTile
            key={p.key}
            preset={p}
            picked={p.key === value}
            sizeMult={sizeMult}
            onPick={() => onChange(p.key)}
          />
        ))}
      </div>
    </section>
  );
}

function SizeSlider({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <Slider
      label="Размер"
      value={value}
      min={SIZE_MIN}
      max={SIZE_MAX}
      step={SIZE_STEP}
      onChange={onChange}
      disabled={disabled}
      low="0,5×"
      high="2,5×"
      format={(v) => `${v.toFixed(2).replace('.', ',')}×`}
    />
  );
}

function PhaseBar({ batch }: { batch: SubtitleBatch }) {
  const current = PHASES.findIndex((p) => p.key === batch.status);
  const broke = batch.status === 'failed';
  const processed = batch.done + batch.failed;
  return (
    <div className={styles.phases}>
      <div className={styles.phaseHead}>
        <b className={styles.phaseTitle}>{batch.title}</b>
        <span className={styles.phaseLabel}>
          {broke ? 'не собралось' : (PHASES[current]?.label ?? STAGE_LABELS[batch.status])}
        </span>
        {batch.status === 'rendering' && (
          <span className={styles.blockNote}>
            готово {processed} из {batch.total}
          </span>
        )}
        <span className={styles.phaseCounter}>
          {batch.done}/{batch.total}
          {batch.failed > 0 ? ` · ${batch.failed} с ошибкой` : ''}
        </span>
      </div>
      <div className={styles.phaseTrack}>
        {PHASES.map((p, i) => {
          const partial =
            i === current && batch.status === 'rendering' && batch.total > 0
              ? Math.round((processed / batch.total) * 100)
              : 0;
          return (
            <div
              key={p.key}
              className={`${styles.phaseSeg} ${styles[`seg_${segState(batch, i, current)}`]}`}
              style={{ flexGrow: p.weight }}
              title={p.label}
            >
              {partial > 0 && <div className={styles.phaseFill} style={{ width: `${partial}%` }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TunePanel({
  batch,
  index,
  preset,
  onPick,
  onClose,
}: {
  batch: SubtitleBatch;
  index: number;
  preset: SubtitlePreset | undefined;
  onPick: (index: number) => void;
  onClose: () => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const item = batch.items[index];
  const [aspect, setAspect] = useState(9 / 16);
  const [size, setSize] = useState(item?.size ?? 1);
  const [pos, setPos] = useState<SubtitlePos | null>(item?.pos ?? null);
  const [dragging, setDragging] = useState(false);
  const layout = useSubtitleLayout();

  useEffect(() => {
    setSize(batch.items[index]?.size ?? 1);
    setPos(batch.items[index]?.pos ?? null);
  }, [index, batch.items]);

  const effectivePos = pos ?? defaultPos(preset, aspect, size);
  const ghostCss = preset ? previewStyle(preset, size) : {};
  const ghostWords = useMemo(
    () =>
      (batch.transcript ?? SAMPLE.join(' '))
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 8),
    [batch.transcript],
  );
  const ghost = useFitWords(ghostWords, ghostCss, frameRef, 0.88).join(' ');

  const apply = (target: number | null) =>
    layout.mutate({
      batchId: batch.id,
      index: target,
      layout: { x: effectivePos.x, y: effectivePos.y, size },
    });

  const moveTo = (clientY: number) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ x: 50, y: clampPct(((clientY - rect.top) / rect.height) * 100) });
  };

  const renderable = batch.items.filter((it) => it.source_alive).length;
  const busy = layout.isPending;

  return (
    <aside className={styles.tune}>
      <div className={styles.tuneHead}>
        <span className={styles.blockTitle}>подгонка</span>
        <span className={styles.blockNote}>{item?.filename}</span>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onClose}
          title="Закрыть"
          aria-label="Закрыть подгонку"
        >
          <X size={14} />
        </button>
      </div>

      <div className={styles.tuneBody}>
        <div className={styles.tuneRail}>
          {batch.items.map((it, i) => (
            <button
              key={`${it.filename}-${i}`}
              type="button"
              className={`${styles.railItem} ${i === index ? styles.railItemOn : ''}`}
              onClick={() => onPick(i)}
              title={it.filename}
              disabled={!it.source_alive || !it.frame}
            >
              {it.frame ? <img src={it.frame.url} alt="" loading="lazy" /> : <FileVideo size={12} />}
              <span>{i + 1}</span>
            </button>
          ))}
        </div>

        <div className={styles.tuneMain}>
          <div
            ref={frameRef}
            className={styles.tuneFrame}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              setDragging(true);
              moveTo(e.clientY);
            }}
            onPointerMove={(e) => dragging && moveTo(e.clientY)}
            onPointerUp={() => setDragging(false)}
            onPointerCancel={() => setDragging(false)}
          >
            {item?.frame && (
              <img
                src={item.frame.url}
                alt="кадр ролика"
                draggable={false}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  if (img.naturalHeight > 0) setAspect(img.naturalWidth / img.naturalHeight);
                }}
              />
            )}
            {preset && (
              <span className={styles.tuneGhost} style={{ ...ghostCss, top: `${effectivePos.y}%` }}>
                {ghost}
              </span>
            )}
            <span className={styles.tunePos}>по вертикали {Math.round(effectivePos.y)}%</span>
          </div>

          <SizeSlider value={size} onChange={setSize} disabled={busy} />

          {layout.error && <ErrorBanner error={layout.error} />}

          <div className={styles.tuneActions}>
            <Button size="sm" variant="primary" onClick={() => apply(index)} disabled={busy}>
              {busy && layout.variables?.index !== null ? 'Рендерим…' : 'Перерендерить ролик'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => apply(null)}
              disabled={busy || renderable < 2}
              title="Поставить эту же позицию и размер всем роликам пачки"
            >
              {busy && layout.variables?.index === null
                ? 'Рендерим пачку…'
                : `Применить ко всем (${renderable})`}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSize(1);
                setPos(null);
              }}
              disabled={busy}
            >
              К пресету
            </Button>
          </div>
          <p className={styles.tuneHint}>
            Тянется только по вертикали — по горизонтали субтитры всегда по центру. Перерендерится
            один этот ролик, остальные переключаются лентой слева.
          </p>
        </div>
      </div>
    </aside>
  );
}
